// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getStoredRef, clearStoredRef } from "../lib/refCapture";

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function loadFacebookSdk() {
  return new Promise((resolve, reject) => {
    if (!FB_APP_ID) return reject(new Error("Facebook não configurado"));
    if (window.FB) return resolve(window.FB);
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FB_APP_ID, cookie: false, xfbml: false, version: "v18.0" });
      resolve(window.FB);
    };
    if (document.getElementById("facebook-jssdk")) return;
    const js = document.createElement("script");
    js.id = "facebook-jssdk"; js.async = true; js.defer = true;
    js.src = "https://connect.facebook.net/pt_BR/sdk.js";
    js.onerror = () => reject(new Error("Falha ao carregar SDK do Facebook"));
    document.body.appendChild(js);
  });
}

function destinationFor(user) {
  if (!user) return "/feed";
  if (user.role === "admin") return "/admin";
  if (user.role === "journalist") return "/jornalista";
  if (user.is_commercial_partner) return "/guia-comercial";
  return "/feed";
}

// Official Google G — colorful SVG
const GoogleGLogo = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1A6.98 6.98 0 015.47 12c0-.73.13-1.44.36-2.1V7.06H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

// Facebook F — white on blue background container
const FacebookFLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="white" aria-hidden>
    <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692V11.09h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.617h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z" />
  </svg>
);

export default function SocialAuthButtons() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [fbReady, setFbReady] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (FB_APP_ID) loadFacebookSdk().then(() => mounted.current && setFbReady(true)).catch(() => {});
    return () => { mounted.current = false; };
  }, []);

  const handleAuthed = async (data) => {
    clearStoredRef();
    await refresh();
    const dest = destinationFor(data?.user);
    toast.success(`Bem-vindo, ${data?.user?.name || ""}!`);
    navigate(dest);
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResp) => {
      if (!tokenResp?.access_token) return;
      setBusy("google");
      try {
        const ref = getStoredRef() || undefined;
        const { data } = await api.post("/auth/google", { access_token: tokenResp.access_token, ref });
        await handleAuthed(data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const message = (detail && typeof detail === "object" ? detail.message : formatApiErrorDetail(detail))
          || "Não foi possível entrar com o Google. Tente novamente.";
        toast.error(message);
      } finally { setBusy(""); }
    },
    onError: () => toast.error("Não foi possível entrar com o Google."),
  });

  const handleFacebookClick = () => {
    if (!fbReady || !window.FB) {
      toast.error("Facebook indisponível no momento. Tente novamente.");
      return;
    }
    setBusy("facebook");
    const ref = getStoredRef() || undefined;
    window.FB.login(async (resp) => {
      if (!resp?.authResponse?.accessToken) {
        setBusy("");
        if (resp?.status !== "unknown") toast.error("Não foi possível entrar com o Facebook.");
        return;
      }
      try {
        const { data } = await api.post("/auth/facebook", { access_token: resp.authResponse.accessToken, ref });
        await handleAuthed(data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail && detail.code === "email_required") {
          const email = window.prompt("Precisamos do seu e-mail para concluir o cadastro:");
          if (email) {
            try {
              const { data } = await api.post("/auth/facebook", {
                access_token: resp.authResponse.accessToken, email, ref,
              });
              await handleAuthed(data);
              return;
            } catch (err2) {
              const d2 = err2?.response?.data?.detail;
              toast.error((d2 && typeof d2 === "object" ? d2.message : formatApiErrorDetail(d2))
                || "Falha ao concluir o cadastro.");
            }
          }
        } else {
          const message = (detail && typeof detail === "object" ? detail.message : formatApiErrorDetail(detail))
            || "Não foi possível entrar com o Facebook.";
          toast.error(message);
        }
      } finally { setBusy(""); }
    }, { scope: "public_profile,email" });
  };

  if (!GOOGLE_CLIENT_ID && !FB_APP_ID) return null;

  // 3D circular buttons — glassmorphism + deep shadow + hover lift
  const base = "group relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 active:scale-90 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40";

  return (
    <div className="flex items-center justify-center gap-6 pt-2" data-testid="social-auth-buttons">
      {GOOGLE_CLIENT_ID && (
        <button
          type="button"
          data-testid="social-google-btn"
          onClick={() => googleLogin()}
          disabled={busy === "google"}
          aria-label="Continuar com Google"
          className={`${base} bg-white hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(60,64,67,0.4)]`}
          style={{
            boxShadow:
              "0 12px 28px -8px rgba(0,0,0,0.28), 0 6px 12px -4px rgba(0,0,0,0.18), inset 0 2px 3px rgba(255,255,255,1), inset 0 -6px 12px rgba(60,64,67,0.10)",
          }}
        >
          <span className="absolute inset-1 rounded-full bg-gradient-to-b from-white via-white to-slate-50 pointer-events-none" />
          <span className="absolute top-1 left-3 right-3 h-3 rounded-full bg-white/80 blur-sm pointer-events-none" />
          <span className="relative z-10">
            {busy === "google" ? (
              <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
            ) : (
              <GoogleGLogo size={36} />
            )}
          </span>
        </button>
      )}

      {FB_APP_ID && (
        <button
          type="button"
          data-testid="social-facebook-btn"
          onClick={handleFacebookClick}
          disabled={busy === "facebook" || !fbReady}
          aria-label="Continuar com Facebook"
          className={`${base} hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(24,119,242,0.55)]`}
          style={{
            background: "linear-gradient(160deg, #4A98F7 0%, #1877F2 45%, #0d5cbf 100%)",
            boxShadow:
              "0 12px 28px -8px rgba(24,119,242,0.45), 0 6px 12px -4px rgba(0,0,0,0.22), inset 0 2px 4px rgba(255,255,255,0.55), inset 0 -8px 14px rgba(0,0,0,0.22)",
          }}
        >
          <span className="absolute top-1 left-3 right-3 h-3 rounded-full bg-white/40 blur-sm pointer-events-none" />
          <span className="absolute bottom-1 left-4 right-4 h-2 rounded-full bg-black/25 blur-sm pointer-events-none" />
          <span className="relative z-10 drop-shadow-[0_2px_2px_rgba(0,0,0,0.35)]">
            {busy === "facebook" ? (
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            ) : (
              <FacebookFLogo size={42} />
            )}
          </span>
        </button>
      )}
    </div>
  );
}
